import React from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import { TAGS, contasParaRespostas, tagsDasRespostas } from '../config/planoCanonico';

/**
 * O QUESTIONARIO DE PERFIL — seis perguntas que decidem QUE CONTAS a empresa
 * tem, e tres que servem para conferir o que ela lancar depois.
 *
 * O desenho antigo pedia "tipo de empresa" (padaria, industria...) e isso
 * confundia TAMANHO com COMPLEXIDADE: a fabrica de fundo de quintal e a
 * multinacional tem o mesmo bloco de custo de producao; a padaria com 100
 * filiais tem o mesmo aluguel cem vezes, nao cem contas novas.
 *
 * Entao aqui nao se pergunta o que a empresa E. Pergunta-se o que ela FAZ, com
 * palavra que o dono usa no balcao. "Voce e industria?" exige que ele se
 * classifique; "voce fabrica ou transforma alguma coisa antes de vender?" ele
 * responde sem pensar.
 *
 * DUAS REGRAS DE TELA
 *
 * 1. Toda pergunta aceita "nao sei", e "nao sei" leva ao caminho MAIS CURTO.
 *    Linha que a pessoa nao entende e linha que ela preenche errado — e errado
 *    e pior que em branco, porque em branco o sistema ja sabe tratar como
 *    ausencia.
 * 2. Quando duas respostas se contradizem, ninguem decide calado: o conflito
 *    aparece na tela e quem resolve e a pessoa.
 */

const OPCOES_VENDE = [
  { valor: TAGS.REVENDE, rotulo: 'Revendo produto pronto', ajuda: 'Compro para revender do jeito que veio.' },
  { valor: TAGS.FABRICA, rotulo: 'Fabrico ou transformo', ajuda: 'Produzo, monto ou transformo antes de vender.' },
  { valor: TAGS.SERVICO, rotulo: 'Presto serviço', ajuda: 'Cobro por hora, projeto ou mensalidade.' }
];

const OPCOES_RECEBE = [
  { valor: TAGS.CARTAO, rotulo: 'Cartão (débito ou crédito)' },
  { valor: TAGS.PRAZO, rotulo: 'Boleto ou a prazo' },
  { valor: TAGS.MARKETPLACE, rotulo: 'App ou marketplace' }
];

const REGIMES = [
  { valor: 'simples', rotulo: 'Simples Nacional' },
  { valor: 'presumido', rotulo: 'Lucro Presumido' },
  { valor: 'real', rotulo: 'Lucro Real' },
  { valor: 'naoSei', rotulo: 'Não sei' }
];

const COMO_IMPOSTO = [
  { valor: 'unico', rotulo: 'Pago uma guia só por mês' },
  { valor: 'separados', rotulo: 'Pago ICMS / ISS / PIS / COFINS separados' },
  { valor: 'naoSei', rotulo: 'Não sei' }
];

export const RESPOSTAS_VAZIAS = {
  vende: [],
  recebe: [],
  temFuncionario: null,
  temDivida: null,
  regime: 'naoSei',
  comoImposto: 'naoSei',
  profundidade: 'grupo',
  contexto: { faturamentoAnual: '', funcionarios: '', maisDeUmPonto: null }
};

/** Pergunta de sim/não/não sei — o "não sei" tem que ser tão fácil quanto os outros. */
function SimNaoNaoSei({ titulo, ajuda, valor, onChange }) {
  const atual = valor === true ? 'sim' : valor === false ? 'nao' : 'naoSei';
  return (
    <FormControl>
      <FormLabel sx={{ fontWeight: 600 }}>{titulo}</FormLabel>
      {ajuda && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>{ajuda}</Typography>
      )}
      <RadioGroup
        row
        value={atual}
        onChange={(e) => onChange(
          e.target.value === 'sim' ? true : e.target.value === 'nao' ? false : null
        )}
      >
        <FormControlLabel value="sim" control={<Radio size="small" />} label="Sim" />
        <FormControlLabel value="nao" control={<Radio size="small" />} label="Não" />
        <FormControlLabel value="naoSei" control={<Radio size="small" />} label="Não sei" />
      </RadioGroup>
    </FormControl>
  );
}

function QuestionarioDePerfil({
  respostas,
  onChange,
  // O QUE O NUMERO CONTA depende de onde este formulario esta.
  //
  // No download, ele conta as linhas do arquivo. No questionario guiado, conta
  // as perguntas — e enquanto ninguem respondeu nada, o catalogo em uso NAO e
  // o recorte curto: e o `essencial`, que e maior. Sem `contagemBase` a tela
  // exibia dois numeros diferentes para a mesma coisa ("33 contas" no topo,
  // "21 linhas" aqui), o que faz o usuario duvidar dos dois.
  substantivo = 'linhas de conta',
  prefixo = 'Sua planilha sai com',
  contagemBase = null
}) {
  const r = { ...RESPOSTAS_VAZIAS, ...respostas };
  const contexto = { ...RESPOSTAS_VAZIAS.contexto, ...(respostas?.contexto || {}) };

  const mudar = (campo, valor) => onChange({ ...r, [campo]: valor });
  const mudarContexto = (campo, valor) =>
    onChange({ ...r, contexto: { ...contexto, [campo]: valor } });

  const alternar = (campo, valor) => {
    const atual = Array.isArray(r[campo]) ? r[campo] : [];
    mudar(campo, atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual, valor]);
  };

  const { conflitos, avisos } = tagsDasRespostas(r);
  const respondeuAlgo = r.vende.length > 0
    || r.recebe.length > 0
    || r.temFuncionario !== null
    || r.temDivida !== null
    || (r.regime && r.regime !== 'naoSei')
    || (r.comoImposto && r.comoImposto !== 'naoSei')
    || r.profundidade === 'conta';

  const quantidadeDeContas = (!respondeuAlgo && contagemBase !== null)
    ? contagemBase
    : contasParaRespostas(r).filter((c) => c.legado).length;

  return (
    <Stack spacing={3}>
      {/* ---------------------------------------------- contexto (conferência) */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Primeiro, três números que você já sabe de cabeça
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Não mudam nada do que vamos pedir. Servem para conferir depois se o que
          você lançar bate — é assim que a gente pega mês faltando e dígito trocado.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
          <TextField
            label="Faturamento por ano (aprox.)"
            placeholder="R$"
            size="small"
            value={contexto.faturamentoAnual}
            onChange={(e) => mudarContexto('faturamentoAnual', e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Quantos funcionários"
            size="small"
            value={contexto.funcionarios}
            onChange={(e) => mudarContexto('funcionarios', e.target.value)}
            sx={{ minWidth: 160 }}
          />
        </Stack>

        <Box sx={{ mt: 1 }}>
          <SimNaoNaoSei
            titulo="Tem mais de um ponto ou loja?"
            valor={contexto.maisDeUmPonto}
            onChange={(v) => {
              // Só pré-marca a profundidade — quem decide é a pergunta 6. Análise
              // por filial ainda não existe no produto, então não se promete.
              onChange({
                ...r,
                contexto: { ...contexto, maisDeUmPonto: v },
                profundidade: v === true ? 'conta' : r.profundidade
              });
            }}
          />
        </Box>
      </Box>

      <Divider />

      {/* ------------------------------------------------- 1. o que vende */}
      <FormControl>
        <FormLabel sx={{ fontWeight: 600 }}>1. O que a empresa vende?</FormLabel>
        <Typography variant="caption" color="text.secondary">Pode marcar mais de uma.</Typography>
        <Stack sx={{ mt: 0.5 }}>
          {OPCOES_VENDE.map((o) => (
            <FormControlLabel
              key={o.valor}
              control={(
                <Checkbox
                  size="small"
                  checked={r.vende.includes(o.valor)}
                  onChange={() => alternar('vende', o.valor)}
                />
              )}
              label={(
                <Box>
                  <Typography variant="body2">{o.rotulo}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.ajuda}</Typography>
                </Box>
              )}
            />
          ))}
        </Stack>
      </FormControl>

      {/* ------------------------------------------------- 2. como recebe */}
      <FormControl>
        <FormLabel sx={{ fontWeight: 600 }}>2. Como o cliente paga?</FormLabel>
        <Typography variant="caption" color="text.secondary">
          Dinheiro e PIX já estão inclusos. Marque o que mais usa.
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ mt: 0.5 }}>
          {OPCOES_RECEBE.map((o) => (
            <FormControlLabel
              key={o.valor}
              control={(
                <Checkbox
                  size="small"
                  checked={r.recebe.includes(o.valor)}
                  onChange={() => alternar('recebe', o.valor)}
                />
              )}
              label={<Typography variant="body2">{o.rotulo}</Typography>}
            />
          ))}
        </Stack>
      </FormControl>

      {/* --------------------------------------------- 3 e 4. equipe e banco */}
      <SimNaoNaoSei
        titulo="3. Tem funcionário registrado em carteira?"
        ajuda="Se só você e os sócios trabalham, marque Não."
        valor={r.temFuncionario}
        onChange={(v) => mudar('temFuncionario', v)}
      />

      <SimNaoNaoSei
        titulo="4. Paga parcela de empréstimo, financiamento, ou usa cheque especial?"
        valor={r.temDivida}
        onChange={(v) => mudar('temDivida', v)}
      />

      {/* ---------------------------------------------------- 5. imposto */}
      <Box>
        <FormControl>
          <FormLabel sx={{ fontWeight: 600 }}>5. Sabe o regime tributário da empresa?</FormLabel>
          <RadioGroup row value={r.regime} onChange={(e) => mudar('regime', e.target.value)}>
            {REGIMES.map((o) => (
              <FormControlLabel
                key={o.valor}
                value={o.valor}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{o.rotulo}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl sx={{ mt: 1 }}>
          <FormLabel sx={{ fontWeight: 600 }}>
            E como o imposto sobre a venda aparece pra você?
          </FormLabel>
          <RadioGroup
            value={r.comoImposto}
            onChange={(e) => mudar('comoImposto', e.target.value)}
          >
            {COMO_IMPOSTO.map((o) => (
              <FormControlLabel
                key={o.valor}
                value={o.valor}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{o.rotulo}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {/* --------------------------------------------------- 6. profundidade */}
      <FormControl>
        <FormLabel sx={{ fontWeight: 600 }}>6. Como você quer lançar?</FormLabel>
        <TextField
          select
          size="small"
          value={r.profundidade}
          onChange={(e) => mudar('profundidade', e.target.value)}
          sx={{ mt: 1, maxWidth: 360 }}
        >
          <MenuItem value="grupo">O total de cada grupo — mais rápido</MenuItem>
          <MenuItem value="conta">Conta por conta — mais detalhe</MenuItem>
        </TextField>
      </FormControl>

      {/* ------------------------------------------- conflitos e resultado */}
      {conflitos.map((c) => (
        <Alert severity="warning" key={c.campo}>{c.pergunta}</Alert>
      ))}
      {avisos.map((a) => (
        <Alert severity="info" key={a}>{a}</Alert>
      ))}

      <Box>
        <Chip
          label={`${prefixo} ${quantidadeDeContas} ${substantivo}`}
          color="primary"
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {respondeuAlgo
            ? 'Só o que você marcou. O que ficar em branco continua em branco — nunca vira zero.'
            : 'Este é o recorte padrão. Responder acima ajusta a lista ao seu negócio.'}
        </Typography>
      </Box>
    </Stack>
  );
}

export default QuestionarioDePerfil;
